<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  provider: { type: String, default: 'openai' },
  apiKey: { type: String, default: '' },
  model: { type: String, default: '' },
  apiKeyConfigured: { type: Boolean, default: false },
  maskedApiKey: { type: String, default: '' },
  usingOwnKey: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:provider',
  'update:apiKey',
  'update:model',
]);

const { t } = useI18n();
const captainConfigStore = useCaptainConfigStore();

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', icon: 'i-ri-openai-fill', available: true },
  {
    id: 'anthropic',
    name: 'Anthropic',
    icon: 'i-ri-anthropic-line',
    available: false,
  },
  {
    id: 'google',
    name: 'Google',
    icon: 'i-woot-gemini',
    available: false,
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    icon: 'i-ph-brain-fill',
    available: false,
  },
];

const isEditing = ref(false);
const localApiKey = ref('');

const hasExistingKey = computed(
  () => props.apiKeyConfigured || props.usingOwnKey
);

const displayKey = computed(
  () => props.maskedApiKey || (hasExistingKey.value ? '••••' : '')
);

const startEditing = () => {
  isEditing.value = true;
  localApiKey.value = '';
};

const cancelEditing = () => {
  isEditing.value = false;
  localApiKey.value = '';
  emit('update:apiKey', '');
};

const handleKeyInput = event => {
  localApiKey.value = event.target.value;
  emit('update:apiKey', event.target.value);
};

const availableModels = computed(() => {
  return captainConfigStore.getModelsForProvider(props.provider);
});

const connectionStatus = computed(() => {
  const result = captainConfigStore.getKeyValidationResult;
  if (captainConfigStore.uiFlags.isValidatingKey) {
    return { type: 'loading', text: t('CAPTAIN_SETTINGS.AI_PROVIDER.TESTING') };
  }
  if (result) {
    if (result.valid) {
      return {
        type: 'success',
        text: t('CAPTAIN_SETTINGS.AI_PROVIDER.STATUS_CONNECTED'),
      };
    }
    return {
      type: 'error',
      text: t('CAPTAIN_SETTINGS.AI_PROVIDER.STATUS_FAILED', {
        error: result.message,
      }),
    };
  }
  if (!props.usingOwnKey && !isEditing.value) {
    return {
      type: 'warning',
      text: t('CAPTAIN_SETTINGS.AI_PROVIDER.STATUS_SYSTEM_KEY'),
    };
  }
  if (props.apiKeyConfigured && !isEditing.value) {
    return {
      type: 'success',
      text: t('CAPTAIN_SETTINGS.AI_PROVIDER.API_KEY_CONFIGURED'),
    };
  }
  return null;
});

const statusClass = computed(() => {
  const s = connectionStatus.value;
  if (!s) return '';
  switch (s.type) {
    case 'success':
      return 'text-n-teal-11 bg-n-teal-3';
    case 'error':
      return 'text-n-ruby-11 bg-n-ruby-3';
    case 'warning':
      return 'text-n-amber-11 bg-n-amber-3';
    case 'loading':
      return 'text-n-slate-11 bg-n-slate-3';
    default:
      return '';
  }
});

const canTestConnection = computed(
  () => isEditing.value ? !!localApiKey.value : hasExistingKey.value
);

const handleTestConnection = async () => {
  if (!canTestConnection.value) return;
  await captainConfigStore.validateKey({
    api_key: isEditing.value ? localApiKey.value : 'use-existing',
    provider: props.provider,
  });
};

const handleProviderChange = event => {
  emit('update:provider', event.target.value);
  captainConfigStore.clearKeyValidation();
};

const handleModelChange = event => {
  emit('update:model', event.target.value);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Banner -->
    <div
      class="flex items-start gap-3 p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
    >
      <Icon icon="i-lucide-info" class="size-5 text-n-amber-11 flex-shrink-0 mt-0.5" />
      <p class="text-sm text-n-amber-11 mb-0">
        {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.BANNER') }}
      </p>
    </div>

    <!-- Provider -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.PROVIDER') }}
      </label>
      <select
        :value="provider"
        class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
        @change="handleProviderChange"
      >
        <option
          v-for="p in PROVIDERS"
          :key="p.id"
          :value="p.id"
          :disabled="!p.available"
        >
          {{ p.name }}
          {{ !p.available ? `(${t('CAPTAIN_SETTINGS.AI_PROVIDER.COMING_SOON')})` : '' }}
        </option>
      </select>
    </div>

    <!-- API Key -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.API_KEY') }}
      </label>
      <div class="flex gap-2">
        <!-- Display mode: key exists and not editing -->
        <div
          v-if="hasExistingKey && !isEditing"
          class="flex items-center justify-between flex-1 h-10 px-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2"
        >
          <span class="text-sm font-mono text-n-slate-11 tracking-wide">
            {{ displayKey }}
          </span>
          <button
            type="button"
            class="text-xs font-medium text-n-brand hover:text-n-brand/80 transition-colors cursor-pointer"
            @click="startEditing"
          >
            {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.CHANGE_KEY') }}
          </button>
        </div>

        <!-- Edit mode: entering a new key -->
        <div v-else class="relative flex-1">
          <input
            type="password"
            :value="localApiKey"
            :placeholder="t('CAPTAIN_SETTINGS.AI_PROVIDER.API_KEY_PLACEHOLDER')"
            class="block w-full h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 placeholder:text-n-slate-10"
            :class="{ 'pe-16': hasExistingKey }"
            @input="handleKeyInput"
          />
          <button
            v-if="hasExistingKey"
            type="button"
            class="absolute inset-y-0 end-0 flex items-center pe-3 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 transition-colors cursor-pointer"
            @click="cancelEditing"
          >
            {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.CANCEL') }}
          </button>
        </div>

        <!-- Test Connection button -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-4 h-10 text-sm font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          :disabled="!canTestConnection || captainConfigStore.uiFlags.isValidatingKey"
          @click="handleTestConnection"
        >
          <Icon
            v-if="captainConfigStore.uiFlags.isValidatingKey"
            icon="i-lucide-loader-2"
            class="size-3.5 animate-spin"
          />
          <Icon v-else icon="i-lucide-plug-zap" class="size-3.5" />
          {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.TEST_CONNECTION') }}
        </button>
      </div>
    </div>

    <!-- Connection Status -->
    <div v-if="connectionStatus" class="flex items-center gap-2">
      <span
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
        :class="statusClass"
      >
        <Icon
          v-if="connectionStatus.type === 'success'"
          icon="i-lucide-check-circle"
          class="size-3.5"
        />
        <Icon
          v-else-if="connectionStatus.type === 'error'"
          icon="i-lucide-x-circle"
          class="size-3.5"
        />
        <Icon
          v-else-if="connectionStatus.type === 'warning'"
          icon="i-lucide-alert-triangle"
          class="size-3.5"
        />
        <Icon
          v-else
          icon="i-lucide-loader-2"
          class="size-3.5 animate-spin"
        />
        {{ connectionStatus.text }}
      </span>
    </div>

    <!-- Model -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.AI_PROVIDER.MODEL') }}
      </label>
      <select
        :value="model"
        class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
        @change="handleModelChange"
      >
        <option value="">
          {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.SELECT_MODEL') }}
        </option>
        <option
          v-for="m in availableModels"
          :key="m.id"
          :value="m.id"
          :disabled="m.coming_soon"
        >
          {{ m.display_name }}
          {{ m.coming_soon ? `(${t('CAPTAIN_SETTINGS.AI_PROVIDER.COMING_SOON')})` : '' }}
        </option>
      </select>
    </div>
  </div>
</template>
